import React, { ReactChild, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { observer } from 'mobx-react';
import { ImperativePanelHandle, Panel } from '@bringg-frontend/resizable-panel';

import ColumnCloseSection from '../column-close-section/column-close-section';

import styles from './column-section.module.scss';

interface ColumnContentProps {
	children: ReactChild;
	collapsible?: boolean;
	sectionName?: string;
	minSize?: number;
	maxSize?: number;
	collapsed?: boolean;
	defaultSize?: number;
	isLastColumn?: boolean;
	order?: number;
}

const ColumnSection = ({
	children,
	collapsed = false,
	collapsible = true,
	sectionName = '',
	minSize = 1,
	maxSize = 100,
	defaultSize,
	isLastColumn,
	order
}: ColumnContentProps) => {
	const [isCollapsed, setIsCollapsed] = useState<boolean>(collapsed);
	const panelRef = useRef<ImperativePanelHandle>(null);

	useEffect(() => {
		let timeoutId: NodeJS.Timeout;

		if (collapsed) {
			timeoutId = setTimeout(() => {
				panelRef?.current?.collapse();
			}, 0);
		}

		return () => {
			if (timeoutId) clearTimeout(timeoutId);
		};
	}, [panelRef, collapsed]);

	useLayoutEffect(() => {
		if (!isLastColumn) {
			const column = document.querySelector(`[data-panel-id="${sectionName}"]`);

			if (column && column.clientWidth < 30) {
				panelRef.current?.collapse();
			}
		}
	}, [isLastColumn, sectionName, minSize, panelRef]);

	return (
		<>
			<Panel
				collapsible={collapsible}
				minSize={minSize}
				maxSize={maxSize}
				ref={panelRef}
				onCollapse={setIsCollapsed}
				className={`${styles.childrenContainer} column-content ${isCollapsed ? 'collapsed' : ''}`}
				defaultSize={defaultSize}
				id={sectionName}
				order={order}
			>
				{children}
			</Panel>
			{isCollapsed && (
				<ColumnCloseSection
					columnName={sectionName}
					openSection={() => {
						panelRef.current?.expand();
					}}
				/>
			)}
		</>
	);
};

export default observer(ColumnSection);
